/**
 * DecisionOptionsBlock - Display decision options with pros/cons
 */

import React, { useState } from 'react'
import { DecisionOptionsBlock as DecisionOptionsBlockType } from '../../../../lib/cockpit/schemas'

interface DecisionOption {
  id: string
  label: string
  description: string
  pros: string[]
  cons: string[]
  expected_outcomes?: string[]
  risks?: string[]
}

interface DecisionOptionsBlockProps {
  data: DecisionOptionsBlockType['data']
  blockId: string
  compact?: boolean
}

function DecisionOptionsBlock({ data, blockId, compact }: DecisionOptionsBlockProps) {
  const [expandedOption, setExpandedOption] = useState<string | null>(
    data.options.length === 1 ? data.options[0].id : null
  )

  if (compact) {
    return (
      <div className="space-y-2">
        {data.options.map((option: DecisionOption) => (
          <div
            key={option.id}
            className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-between"
          >
            <span className="font-medium text-gray-900 dark:text-white">{option.label}</span>
            <div className="flex gap-2 text-xs">
              <span className="text-green-600">+{option.pros.length}</span>
              <span className="text-red-600">-{option.cons.length}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Recommendation */}
      {data.recommendation && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium text-blue-800 dark:text-blue-200">Recommended</span>
          </div>
          <p className="text-blue-900 dark:text-blue-100">{data.recommendation}</p>
          {data.recommendation_rationale && (
            <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              {data.recommendation_rationale}
            </p>
          )}
        </div>
      )}

      {/* Options */}
      <div className="space-y-3">
        {data.options.map((option: DecisionOption) => {
          const isExpanded = expandedOption === option.id
          
          return (
            <div
              key={option.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Header */}
              <button
                onClick={() => setExpandedOption(isExpanded ? null : option.id)}
                className="w-full p-4 flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="font-semibold text-gray-900 dark:text-white">{option.label}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {option.pros.length}
                  </span>
                  <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {option.cons.length}
                  </span>
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{option.description}</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Pros */}
                    <div>
                      <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">Pros</h4>
                      <ul className="space-y-1">
                        {option.pros.map((pro: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Cons */}
                    <div>
                      <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Cons</h4>
                      <ul className="space-y-1">
                        {option.cons.map((con: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Expected outcomes */}
                  {option.expected_outcomes && option.expected_outcomes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expected Outcomes</h4>
                      <ul className="space-y-1">
                        {option.expected_outcomes.map((outcome: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-blue-500">→</span>
                            {outcome}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risks */}
                  {option.risks && option.risks.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-2">Risks</h4>
                      <ul className="space-y-1">
                        {option.risks.map((risk: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <svg className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            {risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DecisionOptionsBlock



